// Villager economy persistence (MB.7). PURE (ENG-1).
//
// A villager's reputation (gossip) and per-offer trade-use counts live in
// boot-side maps keyed by entity id — but entity ids are reassigned on load
// (loadJSON re-spawns), so those maps can't survive a reload on their own. The
// durable home for this state is the entity's own `data` bag (Record<string,
// number>), which round-trips with the entity (SavedEntity.data). This codec
// folds the gossip ledger + use counts into that flat bag and reads them back —
// so after a save/reload the boot maps are rebuilt from each villager's data,
// under whatever new id it was given.
import { type GossipLedger, type GossipType, GOSSIP_TYPES } from './gossip';

const G = 'g_';   // gossip-by-type key prefix in entity data
const U = 'use_'; // per-offer use-count key prefix

/** Fold a villager's gossip + per-offer trade uses into flat entity data (only
 *  non-zero entries are written, so a fresh villager stays clean). */
export function encodeVillagerEconomy(gossip: GossipLedger, uses: readonly number[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of GOSSIP_TYPES) { const v = gossip[t]; if (v) out[G + t] = v; }
  for (let i = 0; i < uses.length; i++) { const u = uses[i]; if (u) out[U + i] = u; }
  return out;
}

/** Read a villager's gossip ledger back out of flat entity data. */
export function decodeGossip(data: Record<string, number>): GossipLedger {
  const g: GossipLedger = {};
  for (const t of GOSSIP_TYPES) { const v = data[G + t]; if (v) g[t as GossipType] = v; }
  return g;
}

/** Read a villager's per-offer use counts back out (length = offerCount). */
export function decodeUses(data: Record<string, number>, offerCount: number): number[] {
  const uses = new Array<number>(Math.max(0, offerCount)).fill(0);
  for (let i = 0; i < uses.length; i++) uses[i] = data[U + i] ?? 0;
  return uses;
}
